/**
 * /risks — global risks dashboard. List of all `RiskIndicator`s with
 * client-side filters (severity, type, project, unresolved-only),
 * severity-then-time sorting, and a row link back to the project.
 *
 * Data source — `GET /api/risks` (returns active risks; toggle
 * "show resolved" issues a second fetch with `include_resolved=true`).
 * Filtering, paging and sorting are entirely client-side: even for the
 * largest installation the list is at most a few hundred entries, and
 * the backend already trims resolved by default.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { AppShell } from "@/layout/AppShell";
import { useRisks } from "@/hooks/useRisks";
import { useProjects } from "@/hooks/useProjects";
import type { RiskIndicator, Severity, RiskType } from "@/api/risks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { RISK_SEVERITY_TONE } from "@/lib/badgeTones";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  SortableTableHead,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/datetime";
import { RisksHelpPopover } from "@/components/risks/RisksHelpPopover";

const SEVERITY_LIST: Severity[] = ["low", "medium", "high", "critical"];
const TYPE_LIST: RiskType[] = [
  "overdue_task",
  "approaching_deadline",
  "resource_overload",
  "dangling_dependency",
  "critical_path_slippage",
];

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

// Цветовая кодировка severity — из централизованного `RISK_SEVERITY_TONE`
// (`lib/badgeTones.ts`). Рендерим через `<Badge tone emphasis>`.

type ProjectChipKey = number | "none";

export function RisksPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [severityToggles, setSeverityToggles] = useState<Set<Severity>>(new Set());
  const [typeToggles, setTypeToggles] = useState<Set<RiskType>>(new Set());
  const [projectToggles, setProjectToggles] = useState<Set<ProjectChipKey>>(new Set());
  const [sorting, setSorting] = useState<SortingState>([{ id: "severity", desc: false }]);

  const { data, isLoading, isError, error, refetch } = useRisks(!unresolvedOnly);
  const { data: projects } = useProjects({ limit: 200 });

  const projectNameById = useMemo(() => {
    const map = new Map<number, string>();
    (projects ?? []).forEach((p) => {
      if (typeof p.id === "number") map.set(p.id, p.name);
    });
    return map;
  }, [projects]);

  // Computed list of projects that actually have risks in the current
  // datasource (analogous to `availableUnits` in PortfolioPage). Plus a
  // synthetic "none" key if any risk has `project_id === null`.
  const availableProjects = useMemo<{ id: number; name: string }[]>(() => {
    const ids = new Set<number>();
    (data ?? []).forEach((r) => {
      if (typeof r.project_id === "number") ids.add(r.project_id);
    });
    return [...ids]
      .map((id) => ({ id, name: projectNameById.get(id) ?? `#${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, projectNameById]);

  const hasOrphanRisks = useMemo<boolean>(
    () => (data ?? []).some((r) => r.project_id === null || r.project_id === undefined),
    [data],
  );

  const rows = useMemo<RiskIndicator[]>(() => {
    const all = data ?? [];
    return all.filter((r) => {
      if (severityToggles.size > 0 && !severityToggles.has(r.severity)) return false;
      if (typeToggles.size > 0 && !typeToggles.has(r.risk_type)) return false;
      if (projectToggles.size > 0) {
        const key: ProjectChipKey =
          r.project_id === null || r.project_id === undefined ? "none" : r.project_id;
        if (!projectToggles.has(key)) return false;
      }
      return true;
    });
  }, [data, severityToggles, typeToggles, projectToggles]);

  const toggleSeverity = (s: Severity): void => {
    setSeverityToggles((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };
  const toggleType = (rt: RiskType): void => {
    setTypeToggles((prev) => {
      const next = new Set(prev);
      if (next.has(rt)) next.delete(rt);
      else next.add(rt);
      return next;
    });
  };
  const toggleProject = (key: ProjectChipKey): void => {
    setProjectToggles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const columns = useMemo<ColumnDef<RiskIndicator>[]>(
    () => [
      {
        id: "severity",
        header: t("risks.columns.severity"),
        accessorFn: (r) => SEVERITY_RANK[r.severity],
        cell: ({ row }) => {
          const spec = RISK_SEVERITY_TONE[row.original.severity];
          return (
            <Badge tone={spec.tone} emphasis={spec.emphasis}>
              {t(`risks.severity.${row.original.severity}`)}
            </Badge>
          );
        },
      },
      {
        id: "type",
        header: t("risks.columns.type"),
        accessorFn: (r) => t(`risks.type.${r.risk_type}`),
        cell: ({ row }) => (
          <span className="text-sm font-medium">{t(`risks.type.${row.original.risk_type}`)}</span>
        ),
      },
      {
        id: "description",
        header: t("risks.columns.description"),
        accessorKey: "description",
        cell: ({ row }) =>
          row.original.description ? (
            <span className="text-muted-foreground text-sm">{row.original.description}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "project",
        header: t("risks.columns.project"),
        accessorFn: (r) =>
          r.project_id != null ? (projectNameById.get(r.project_id) ?? `#${r.project_id}`) : "",
        cell: ({ row }) => {
          const pid = row.original.project_id;
          if (pid == null)
            return <span className="text-muted-foreground">{t("risks.noProject")}</span>;
          const name = projectNameById.get(pid) ?? `#${pid}`;
          return (
            <Link
              to="/projects/$projectId"
              params={{ projectId: String(pid) }}
              search={{ tab: "risks" }}
              data-testid={`risk-project-link-${pid}`}
              className="text-primary text-sm hover:underline"
            >
              {name}
            </Link>
          );
        },
      },
      {
        id: "detected_at",
        header: t("risks.columns.detectedAt"),
        accessorKey: "detected_at",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {formatDateTime(row.original.detected_at, i18n.resolvedLanguage)}
          </span>
        ),
      },
      {
        id: "status",
        header: t("risks.columns.status"),
        accessorFn: (r) => (r.is_resolved ? 1 : 0),
        cell: ({ row }) =>
          row.original.is_resolved ? (
            <span className="text-[var(--tone-success-fg)] text-xs">
              {row.original.resolved_at
                ? t("risks.resolvedAt", {
                    when: formatDateTime(row.original.resolved_at, i18n.resolvedLanguage),
                  })
                : t("risks.status.resolved")}
            </span>
          ) : (
            <span className="text-xs">{t("risks.status.active")}</span>
          ),
      },
    ],
    [t, i18n.resolvedLanguage, projectNameById],
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } },
  });

  const resetFilters = (): void => {
    setSeverityToggles(new Set());
    setTypeToggles(new Set());
    setProjectToggles(new Set());
    setUnresolvedOnly(true);
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1">
          <h1 className="text-2xl font-semibold" data-testid="risks-title">
            {t("risks.title")}
          </h1>
          <RisksHelpPopover iconSize="md" />
        </div>
        <span className="text-muted-foreground text-sm" data-testid="risks-count">
          {t("risks.countTotal", { count: rows.length })}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {/* 1. Severity */}
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="risks-severity-filter"
          aria-label={t("risks.filters.severity")}
        >
          <span className="text-muted-foreground text-xs uppercase">
            {t("risks.filters.severity")}
          </span>
          {SEVERITY_LIST.map((s) => {
            const active = severityToggles.has(s);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={active}
                data-testid={`risk-severity-${s}`}
                onClick={() => toggleSeverity(s)}
                className={`rounded-full px-2 py-1 text-xs font-medium transition ${(() => {
                  const sp = RISK_SEVERITY_TONE[s];
                  return sp.emphasis === "solid" ? `tone-${sp.tone}-solid` : `tone-${sp.tone}`;
                })()} ${active ? "ring-foreground ring-2 ring-offset-1" : "opacity-70 hover:opacity-100"}`}
              >
                {t(`risks.severity.${s}`)}
              </button>
            );
          })}
          {severityToggles.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              data-testid="risks-severity-clear"
              onClick={() => setSeverityToggles(new Set())}
            >
              {t("risks.filters.clear")}
            </Button>
          )}
        </div>

        {/* 2. Type */}
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="risks-type-filter"
          aria-label={t("risks.filters.type")}
        >
          <span className="text-muted-foreground text-xs uppercase">{t("risks.filters.type")}</span>
          {TYPE_LIST.map((rt) => {
            const active = typeToggles.has(rt);
            return (
              <button
                key={rt}
                type="button"
                aria-pressed={active}
                data-testid={`risk-type-${rt}`}
                onClick={() => toggleType(rt)}
                className={`bg-muted text-foreground rounded-full px-2 py-1 text-xs font-medium transition ${
                  active ? "ring-foreground ring-2 ring-offset-1" : "opacity-70 hover:opacity-100"
                }`}
              >
                {t(`risks.type.${rt}`)}
              </button>
            );
          })}
          {typeToggles.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              data-testid="risks-type-clear"
              onClick={() => setTypeToggles(new Set())}
            >
              {t("risks.filters.clear")}
            </Button>
          )}
        </div>

        {/* 3. Project — computed list of projects that have risks */}
        {(availableProjects.length > 0 || hasOrphanRisks) && (
          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="risks-project-filter"
            aria-label={t("risks.filters.project")}
          >
            <span className="text-muted-foreground text-xs uppercase">
              {t("risks.filters.project")}
            </span>
            {availableProjects.map((p) => {
              const active = projectToggles.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={active}
                  data-testid={`risk-project-${p.id}`}
                  onClick={() => toggleProject(p.id)}
                  className={`bg-muted text-foreground rounded-full px-2 py-1 text-xs font-medium transition ${
                    active ? "ring-foreground ring-2 ring-offset-1" : "opacity-70 hover:opacity-100"
                  }`}
                >
                  {p.name}
                </button>
              );
            })}
            {hasOrphanRisks && (
              <button
                type="button"
                aria-pressed={projectToggles.has("none")}
                data-testid="risk-project-none"
                onClick={() => toggleProject("none")}
                className={`bg-muted text-foreground rounded-full px-2 py-1 text-xs font-medium transition ${
                  projectToggles.has("none")
                    ? "ring-foreground ring-2 ring-offset-1"
                    : "opacity-70 hover:opacity-100"
                }`}
              >
                {t("risks.noProjectChip")}
              </button>
            )}
            {projectToggles.size > 0 && (
              <Button
                size="sm"
                variant="ghost"
                data-testid="risks-project-clear"
                onClick={() => setProjectToggles(new Set())}
              >
                {t("risks.filters.clear")}
              </Button>
            )}
          </div>
        )}

        {/* 4. Active-only toggle + reset */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={unresolvedOnly ? "default" : "outline"}
            size="sm"
            aria-pressed={unresolvedOnly}
            data-testid="risks-toggle-unresolved"
            onClick={() => setUnresolvedOnly((v) => !v)}
          >
            {unresolvedOnly
              ? t("risks.filters.unresolvedOnly")
              : t("risks.filters.includeResolved")}
          </Button>
          <Button variant="ghost" size="sm" data-testid="risks-reset" onClick={resetFilters}>
            {t("risks.filters.reset")}
          </Button>
        </div>
      </div>

      <div className="mt-4 w-fit min-w-full rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)]">
        <Table containerClassName="overflow-x-visible">
          {/* sticky-шапка прижата под TopBar: отрицательный top гасит верхний
              p-6 у <main>-скроллера; bg должен совпадать с интерьером контейнера. */}
          <TableHeader className="sticky top-[calc(var(--spacing)*-6)] z-20 border-b border-[var(--rule-line)] bg-[var(--bg-panel-0)]">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <SortableTableHead
                    key={h.id}
                    sortable={h.column.getCanSort()}
                    direction={h.column.getIsSorted()}
                    onToggle={h.column.getToggleSortingHandler()}
                    data-testid={`risks-sort-${h.column.id}`}
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </SortableTableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-muted-foreground py-6 text-center"
                >
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-6 text-center">
                  <ApiErrorNotice
                    error={error}
                    fallbackMessage={t("common.apiError.fallback")}
                    className="text-destructive"
                  />
                  <Button variant="ghost" size="sm" className="ml-2" onClick={() => void refetch()}>
                    {t("common.retry")}
                  </Button>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-muted-foreground py-6 text-center"
                  data-testid="risks-empty"
                >
                  {t("risks.empty")}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-testid={`risk-row-${row.original.id ?? "anon"}`}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {table.getPageCount() > 1 && (
          /* закреплённый футер пагинации; отрицательный bottom гасит нижний p-6. */
          <div className="sticky bottom-[calc(var(--spacing)*-6)] z-20 flex items-center justify-end gap-2 rounded-b-[var(--radius-card)] border-t bg-[var(--bg-panel-0)] px-2 py-2 text-sm">
            <span className="text-muted-foreground" data-testid="risks-page-indicator">
              {t("projects.pageIndicator", {
                page: table.getState().pagination.pageIndex + 1,
                total: table.getPageCount(),
              })}
            </span>
            <Button
              variant="outline"
              size="sm"
              data-testid="risks-prev"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              {t("projects.prev")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="risks-next"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              {t("projects.next")}
            </Button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
